import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { toast } from "sonner"
import {
  ChevronLeft, ChevronRight, Check, Trash2,
  Dumbbell, Play, Pencil, Move, Lock, Plus, ArrowLeft, ClipboardList, X,
} from "lucide-react"
import { useAnalytics } from "@/hooks/useAnalytics"
import {
  useCalendarEvents,
  getMonthBounds,
  getMonthGrid,
  getMonthLabel,
  isDateToday,
  type CalendarCell,
} from "@/hooks/useCalendarEvents"
import {
  ACTIVITY_EVENT_TYPES,
  formatUnlockTime,
  getActivityEndAt,
  getActivityPostSessionAnswer,
  isSuiviSeul,
  type CalendarEventRow,
  type CalendarEventType,
} from "@/lib/supabase"
import { useAuth } from "@/contexts/AuthContext"
import { useSubscription } from "@/hooks/useSubscription"
import { useNextProgramSession } from "@/hooks/useNextProgramSession"
import { BottomNav } from "@/components/BottomNav"
import { OnboardingTip } from "@/components/OnboardingTip"
import { useOnboarding } from "@/hooks/useOnboarding"
import {
  AddEventSteps,
  CalendarSheet,
  TimeWheelPicker,
  formatDateLong,
  type AddEventValues,
} from "@/components/calendar/AddEventFlow"

const DAY_LABELS = ["Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam.", "Dim."]

// ============================================
// COLORS & METADATA PAR TYPE
// ============================================

// Deux familles de glyphes cohabitent ici, et c'est voulu :
//   — seance-planned / seance-done sont les seances de Zeyone : l'halter Lucide, le meme
//     pictogramme que partout ailleurs dans l'app pour designer "une seance du programme" ;
//   — les trois types que le client ajoute lui-meme portent une illustration 64x64
//     (public/illustrations), au meme niveau de finition que les tuiles Poids/Taille/IMC du
//     Profil. Ce sont les trois options du menu "Que veux-tu ajouter ?", vues cote a cote et
//     de pres : un pictogramme au trait y tombait a plat.
const TYPE_CONFIG: Record<
  CalendarEventType,
  {
    color: string; bg: string; border: string; label: string
    icon?: React.ComponentType<{ className?: string }>
    /** Chemin public d'une illustration 64x64 — prioritaire sur `icon`. */
    illustration?: string
  }
> = {
  "seance-planned": {
    color: "#06b6d4",
    bg: "rgba(6,182,212,0.16)",
    border: "rgba(6,182,212,0.55)",
    icon: Dumbbell,
    label: "Séance planifiée",
  },
  "seance-done": {
    color: "#06b6d4",
    bg: "rgba(6,182,212,0.16)",
    border: "rgba(6,182,212,0.55)",
    icon: Dumbbell,
    label: "Séance terminée",
  },
  "seance-libre": {
    color: "#a78bfa",
    bg: "rgba(139,92,246,0.16)",
    border: "rgba(139,92,246,0.55)",
    illustration: "/illustrations/seance-libre.svg",
    label: "Séance libre",
  },
  "activite": {
    color: "#cbd5e1",
    bg: "rgba(203,213,225,0.12)",
    border: "rgba(203,213,225,0.4)",
    illustration: "/illustrations/activite-sportive.svg",
    label: "Activité sportive",
  },
  "recuperation": {
    color: "#fb923c",
    bg: "rgba(249,115,22,0.16)",
    border: "rgba(249,115,22,0.55)",
    illustration: "/illustrations/recuperation-active.svg",
    label: "Récupération active",
  },
}

/**
 * Glyphe d'un type d'evenement.
 *
 * L'illustration ne recoit PAS de pastille teintee derriere elle : elle porte deja sa
 * couleur, son volume et son ombre portee — meme regle que les tuiles Poids/Taille/IMC et
 * les lignes de mesures du Profil, ou une pastille en plus ne faisait que rapetisser le
 * dessin de 4 px. L'icone Lucide, elle, est un trait plat : elle garde sa pastille.
 */
function TypeGlyph({ type }: { type: CalendarEventType }) {
  const cfg = TYPE_CONFIG[type]
  if (cfg.illustration) {
    return <img src={cfg.illustration} alt="" aria-hidden className="w-10 h-10 shrink-0" />
  }
  const Icon = cfg.icon!
  return (
    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${cfg.color}30` }}>
      <Icon className="w-5 h-5" />
    </div>
  )
}

// ============================================
// MODAL STATE
// ============================================

type ModalMode =
  | { kind: "day-view"; date: string }          // Clic sur jour avec événement
  | { kind: "day-empty"; date: string }         // Clic sur jour vide
  | { kind: "add"; date: string; type: CalendarEventType }     // Titre → heure de fin → contenu (séance libre)
  | { kind: "edit-time"; eventId: string; currentTime: string; eventType: CalendarEventType }

interface CalendarPageProps {
  email: string
  firstName?: string
}

export const CalendarPage = ({}: CalendarPageProps) => {
  const navigate = useNavigate()
  const [currentDate, setCurrentDate] = useState(new Date())

  const { start: monthStart, end: monthEnd } = useMemo(
    () => getMonthBounds(currentDate),
    [currentDate]
  )

  const { events: allEvents, loading, error, addEvent, editEvent, removeEvent } = useCalendarEvents(
    monthStart,
    monthEnd
  )

  // Suivi seul : aucune séance Zeyone à faire. La base n'en planifie plus et a retiré
  // celles qui restaient ; le filtre couvre une ligne restée en place (séance faite dont
  // la bascule en « seance-done » a échoué). Les séances faites restent affichées.
  const { wizardData } = useAuth()
  const suiviSeul = isSuiviSeul(wizardData)
  const events = useMemo(
    () => (suiviSeul ? allEvents.filter((e) => e.type !== "seance-planned") : allEvents),
    [allEvents, suiviSeul]
  )

  const { track } = useAnalytics()
  const { isActive, trialEnded } = useSubscription()
  const { nextSessionNumber, nextSessionLabel, loaded: nextPsnLoaded } = useNextProgramSession()
  const { state: onboardingState, loading: onboardingLoading, markTipSeen } = useOnboarding()

  const grid = useMemo(() => getMonthGrid(currentDate), [currentDate])

  // Colonne où la 1ère ligne de la grille bascule des jours hors-mois (gauche) aux jours
  // du mois (droite) — ex. Ven 31 -> Sam 1er. -1 si le mois commence un lundi (pas de bascule).
  const leadingTransitionCol = useMemo(() => {
    for (let c = 1; c < 7; c++) {
      if (!grid[c - 1]?.isCurrentMonth && grid[c]?.isCurrentMonth) return c
    }
    return -1
  }, [grid])

  // Regroupe les events par date ISO
  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEventRow[]> = {}
    for (const e of events) {
      if (!map[e.event_date]) map[e.event_date] = []
      map[e.event_date].push(e)
    }
    return map
  }, [events])

  // Modal state
  const [modal, setModal] = useState<ModalMode | null>(null)
  const [moveModeEventId, setMoveModeEventId] = useState<string | null>(null)

  // Form state (modification de l'heure — l'ajout gère ses propres champs)
  const [hourInput, setHourInput] = useState("12")
  const [minuteInput, setMinuteInput] = useState("00")
  const [saving, setSaving] = useState(false)

  // ============================================
  // Actions
  // ============================================

  const handlePrevMonth = () => {
    setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))
  }
  const handleNextMonth = () => {
    setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))
  }

  const handleDayClick = (cell: CalendarCell) => {
    if (moveModeEventId) {
      handleMoveEvent(cell.iso)
      return
    }

    const dayEvents = eventsByDate[cell.iso] ?? []
    if (dayEvents.length === 0) {
      // Lecture seule si pas d'abonnement actif — pas de création
      if (!isActive) {
        toast.info("Abonnement requis", { description: "Souscris pour créer des événements." })
        return
      }
      setModal({ kind: "day-empty", date: cell.iso })
    } else {
      setModal({ kind: "day-view", date: cell.iso })
    }
  }

  const handleMoveEvent = async (newDate: string) => {
    if (!moveModeEventId) return
    setSaving(true)
    const { error: err } = await editEvent(moveModeEventId, { event_date: newDate })
    if (err) {
      toast.error("Erreur", { description: err })
    } else {
      toast.success("Événement déplacé")
    }
    setMoveModeEventId(null)
    setSaving(false)
  }

  const handleStartAdd = (type: CalendarEventType) => {
    if (modal?.kind !== "day-empty") return
    setModal({ kind: "add", date: modal.date, type })
  }

  const handleSubmitAdd = async ({ title, endTime, content }: AddEventValues) => {
    if (modal?.kind !== "add") return
    setSaving(true)
    const { error: err } = await addEvent({
      type: modal.type,
      title,
      event_date: modal.date,
      end_time: endTime,
      ...(content ? { content } : {}),
    })
    if (err) {
      toast.error("Erreur", { description: err })
    } else {
      track('calendar_event_created', { type: modal.type })
      toast.success("Ajouté au calendrier")
      setModal(null)
    }
    setSaving(false)
  }

  const handleEditTime = (event: CalendarEventRow) => {
    const time = event.end_time ?? "12:00:00"
    const [h, m] = time.split(":")
    setHourInput(h)
    setMinuteInput(m)
    setModal({ kind: "edit-time", eventId: event.id, currentTime: time, eventType: event.type })
  }

  const handleSubmitEditTime = async () => {
    if (modal?.kind !== "edit-time") return
    setSaving(true)
    const endTime = `${hourInput.padStart(2, "0")}:${minuteInput.padStart(2, "0")}:00`
    const { error: err } = await editEvent(modal.eventId, { end_time: endTime })
    if (err) {
      toast.error("Erreur", { description: err })
    } else {
      toast.success("Heure mise à jour")
      setModal(null)
    }
    setSaving(false)
  }

  const handleStartMove = (eventId: string) => {
    setMoveModeEventId(eventId)
    setModal(null)
    toast.info("Clique sur une nouvelle date", {
      description: "Ou appuie sur Échap pour annuler",
    })
  }

  const handleDelete = async (eventId: string) => {
    setSaving(true)
    const { error: err } = await removeEvent(eventId)
    if (err) {
      toast.error("Erreur", { description: err })
    } else {
      toast.success("Événement supprimé")
      setModal(null)
    }
    setSaving(false)
  }

  // ============================================
  // Render
  // ============================================

  const todayIso = useMemo(() => {
    const t = new Date()
    const y = t.getFullYear()
    const m = String(t.getMonth() + 1).padStart(2, "0")
    const d = String(t.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }, [])

  const showMoveTip =
    !onboardingLoading && !!onboardingState && !onboardingState.tip_calendar_move_seen &&
    isActive && !moveModeEventId &&
    events.some((e) => e.type === "seance-planned" && e.event_date >= todayIso)

  return (
    <div className="min-h-screen bg-[#060a13] pb-24 cursor-default relative">

      {/* HEADER */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="px-4 pt-5 pb-3 relative z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 shrink-0 min-w-0">
            <button onClick={() => navigate("/dashboard")} className="w-8 h-8 rounded-lg bg-gray-800/50 flex items-center justify-center hover:bg-gray-700/50 cursor-pointer shrink-0">
              <ArrowLeft className="w-4 h-4 text-gray-400" />
            </button>
            <h1 className="text-[20px] font-bold text-white tracking-tight whitespace-nowrap">Calendrier</h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={handlePrevMonth} className="w-8 h-8 rounded-lg bg-gray-800/50 flex items-center justify-center hover:bg-gray-700/50 transition-colors cursor-pointer shrink-0">
              <ChevronLeft className="w-3.5 h-3.5 text-gray-300" />
            </button>
            <div className="px-1 text-center">
              <span className="text-[12px] font-semibold text-white whitespace-nowrap">{getMonthLabel(currentDate)}</span>
            </div>
            <button onClick={handleNextMonth} className="w-8 h-8 rounded-lg bg-gray-800/50 flex items-center justify-center hover:bg-gray-700/50 transition-colors cursor-pointer shrink-0">
              <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* ASTUCE « DÉPLACER » — une seule fois, quand des séances planifiées sont à l'écran.
          Les 4 séances d'une vague se placent seules sur lun/mar/jeu/ven : le client doit
          savoir qu'il peut en changer le jour (lot 3). Seulement si l'écriture est permise. */}
      {showMoveTip && (
        <OnboardingTip
          message="Ces jours ne te conviennent pas ? Touche une séance, puis « Déplacer », et choisis un autre jour."
          onDismiss={() => markTipSeen("tip_calendar_move_seen")}
        />
      )}

      {/* MOVE MODE BANNER */}
      {moveModeEventId && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="mx-4 mb-3 p-3 rounded-xl flex items-center gap-3"
          style={{ background: "rgba(6,182,212,0.10)", border: "1px solid rgba(6,182,212,0.25)" }}>
          <Move className="w-4 h-4 text-cyan-400 shrink-0" />
          <p className="text-[13px] text-cyan-400 flex-1">Sélectionne une nouvelle date</p>
          <button onClick={() => setMoveModeEventId(null)} className="text-[12px] text-gray-400 underline cursor-pointer">
            Annuler
          </button>
        </motion.div>
      )}

      {/* ERROR STATE */}
      {error && (
        <div className="mx-4 mb-3 p-3 rounded-xl text-[13px]" style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171" }}>
          {error}
        </div>
      )}

      {/* DAY HEADERS */}
      <div className="px-4 grid grid-cols-7 gap-0 mb-1">
        {DAY_LABELS.map((d, i) => {
          const todayWeekday = (new Date().getDay() + 6) % 7 // lundi=0
          const isActive = i === todayWeekday
          return (
            <div key={d} className="text-center py-2">
              <span className={`text-[11px] font-semibold tracking-wider ${isActive ? "text-cyan-400" : "text-gray-500"}`}>
                {d}
              </span>
            </div>
          )
        })}
      </div>

      {/* GRID */}
      <div className="px-4 relative z-10">
        <div className="grid grid-cols-7 gap-0"
          style={{ background: "rgba(255,255,255,0.015)", border: "2px solid rgba(71,85,105,0.55)", borderRadius: "22px", overflow: "hidden" }}>
          {grid.map((cell, idx) => {
            const dayEvents = eventsByDate[cell.iso] ?? []
            const isToday = cell.iso === todayIso
            const isEmpty = dayEvents.length === 0
            const col = idx % 7
            const rowIndex = Math.floor(idx / 7)
            const isFirstRow = rowIndex === 0
            const isSecondRow = rowIndex === 1
            const isLastRow = idx >= grid.length - 7
            // Bordure gauche dédiée : sur toute la hauteur de la colonne de transition, la
            // bordure vient uniquement de la cellule "mois en cours" (jamais atténuée par
            // l'opacity-25 du hors-mois), pour une ligne continue et alignée du haut en bas.
            const isTransitionColRight = col === leadingTransitionCol
            const isTransitionColLeft = col === leadingTransitionCol - 1
            // Frontière ligne 1/ligne 2 : la 1ère ligne peut mélanger jours hors-mois (atténués)
            // et jours du mois. Pour que le trait soit une ligne continue parfaitement alignée
            // sur les 7 colonnes, on le fait porter entièrement par la 2e ligne (toujours dans
            // le mois, jamais atténuée) plutôt que de le répartir entre les deux lignes.
            return (
              <button
                key={idx}
                onClick={() => handleDayClick(cell)}
                className={`relative text-left p-2 cursor-pointer transition-all hover:bg-white/[0.03] ${!cell.isCurrentMonth ? "opacity-25" : ""}`}
                style={{
                  minHeight: 122,
                  borderLeft: isTransitionColRight ? "2px solid rgba(71,85,105,0.55)" : "none",
                  borderTop: isSecondRow ? "2px solid rgba(71,85,105,0.55)" : "none",
                  borderRight: (idx + 1) % 7 !== 0 && !isTransitionColLeft ? "2px solid rgba(71,85,105,0.55)" : "none",
                  borderBottom: (!isLastRow && !isFirstRow) ? "2px solid rgba(71,85,105,0.55)" : "none",
                }}
              >
                {/* Day number */}
                <div className="flex items-center justify-center mb-2">
                  {isToday ? (
                    <div className="w-[24px] h-[24px] rounded-full flex items-center justify-center"
                      style={{ background: "#06b6d4", boxShadow: "0 0 10px rgba(6,182,212,0.5)" }}>
                      <span className="text-[11px] font-bold text-[#060a13]">{cell.dayNumber}</span>
                    </div>
                  ) : (
                    <span className="text-[12px] font-semibold text-gray-300">{cell.dayNumber}</span>
                  )}
                </div>

                {isEmpty ? (
                  cell.isCurrentMonth && (
                    <div className="flex items-center justify-center">
                      <div className="w-[18px] h-[18px] rounded-full flex items-center justify-center"
                        style={{ border: "1.2px solid rgba(148,163,184,0.35)" }}>
                        <Plus className="w-2.5 h-2.5" style={{ color: "rgba(148,163,184,0.6)" }} />
                      </div>
                    </div>
                  )
                ) : (
                  /* Events badges */
                  <div className="flex flex-col gap-1">
                    {dayEvents.slice(0, 3).map((e) => {
                      const cfg = TYPE_CONFIG[e.type]
                      return (
                        <div key={e.id} className="flex items-center gap-1 px-1.5 py-[3px] rounded-md overflow-hidden"
                          style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                          <span className="text-[9px] font-bold truncate" style={{ color: cfg.color }}>
                            {e.title}
                          </span>
                          {e.type === "seance-done" && (
                            <Check className="w-2.5 h-2.5 text-emerald-400 shrink-0 ml-auto" />
                          )}
                        </div>
                      )
                    })}
                    {dayEvents.length > 3 && (
                      <span className="text-[9px] text-gray-500 px-1">+{dayEvents.length - 3}</span>
                    )}
                  </div>
                )}
              </button>
            )
          })}
        </div>

        {loading && (
          <p className="text-[12px] text-gray-500 text-center mt-3">Chargement…</p>
        )}
      </div>

      {/* INFO CARD + LEGEND */}
      <div className="px-4 mt-4">
        <div className="rounded-2xl p-4"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)" }}>
          <p className="text-[12.5px] text-gray-400 leading-relaxed">
            {suiviSeul
              ? "Ajoute tes séances — séance libre, sport habituel, récupération active. Touche un jour pour en créer une : un questionnaire suit l'heure de fin pour suivre ta charge."
              : "En plus des séances planifiées par Zeyone, tu peux ajouter tes séances personnelles — sport habituel, récupération active, séance libre. Elles s'affichent directement dans ton calendrier."}
          </p>
          <div className="flex flex-wrap gap-2 mt-3 pt-3"
            style={{ borderTop: "1px solid rgba(148,163,184,0.10)" }}>
            {((suiviSeul
              ? ["seance-libre", "activite", "recuperation"]
              : ["seance-planned", "seance-libre", "activite", "recuperation"]) as CalendarEventType[]).map((t) => {
              const cfg = TYPE_CONFIG[t]
              return (
                <div key={t} className="px-2 py-1 rounded-md"
                  style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                  <span className="text-[10px] font-semibold" style={{ color: cfg.color }}>{cfg.label}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* BOTTOM SHEET MODALS */}
      {/* ============================================ */}

      <CalendarSheet open={!!modal} onClose={() => !saving && setModal(null)}>
          {modal && (
            <>
              {/* DAY EMPTY — 3 options */}
              {modal.kind === "day-empty" && (
                <>
                  <h3 className="text-[18px] font-bold text-white mb-1 text-center">{formatDateLong(modal.date)}</h3>
                  <p className="text-[13px] text-gray-500 mb-5 text-center">Que veux-tu ajouter ?</p>
                  <div className="flex flex-col gap-2.5">
                    {(["seance-libre", "activite", "recuperation"] as CalendarEventType[]).map((t) => {
                      const cfg = TYPE_CONFIG[t]
                      return (
                        <span
                          key={t}
                          title={!isActive ? "Abonnement requis pour créer des événements" : undefined}
                          className={!isActive ? "cursor-not-allowed" : undefined}
                        >
                          <button
                            onClick={() => handleStartAdd(t)}
                            disabled={!isActive}
                            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all w-full disabled:opacity-40 disabled:pointer-events-none ${isActive ? "cursor-pointer active:scale-[0.98]" : ""}`}
                            style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}
                          >
                            <TypeGlyph type={t} />
                            <span className="text-[14px] font-semibold flex-1 text-left" style={{ color: cfg.color }}>{cfg.label}</span>
                            <ChevronRight className="w-4 h-4" style={{ color: cfg.color }} />
                          </button>
                        </span>
                      )
                    })}
                  </div>
                </>
              )}

              {/* DAY VIEW — list events */}
              {modal.kind === "day-view" && (
                <>
                  <h3 className="text-[18px] font-bold text-white mb-4 text-center">{formatDateLong(modal.date)}</h3>
                  <div className="flex flex-col gap-2.5">
                    {(eventsByDate[modal.date] ?? []).map((e) => (
                      <EventCard
                        key={e.id}
                        event={e}
                        isToday={isDateToday(modal.date)}
                        canWrite={isActive}
                        trialEnded={trialEnded}
                        nextSessionNumber={nextSessionNumber}
                        nextSessionLabel={nextSessionLabel}
                        nextPsnLoaded={nextPsnLoaded}
                        onStartSession={() => { setModal(null); navigate("/dashboard/session") }}
                        onEditTime={() => handleEditTime(e)}
                        onMove={() => handleStartMove(e.id)}
                        onDelete={() => handleDelete(e.id)}
                        saving={saving}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* ADD — titre, heure de fin, contenu (séance libre) */}
              {modal.kind === "add" && (
                <AddEventSteps
                  date={modal.date}
                  type={modal.type}
                  typeLabel={TYPE_CONFIG[modal.type].label}
                  saving={saving}
                  onCancel={() => setModal(null)}
                  onSubmit={handleSubmitAdd}
                />
              )}

              {/* EDIT TIME */}
              {modal.kind === "edit-time" && (
                <>
                  <h3 className="text-[18px] font-bold text-white mb-1 text-center">
                    {modal.eventType === "seance-planned"
                      ? "Heure de début"
                      : "Heure de fin prévue"}
                  </h3>
                  <p className="text-[12px] text-gray-500 mb-5 text-center">
                    {modal.eventType === "seance-planned"
                      ? "La séance démarrera à l'heure choisie"
                      : "Questionnaire envoyé 30 min plus tard"}
                  </p>

                  <TimeWheelPicker
                    hour={hourInput}
                    minute={minuteInput}
                    onHourChange={setHourInput}
                    onMinuteChange={setMinuteInput}
                    recapLabel={modal.eventType === "seance-planned" ? "Début prévu" : "Fin prévue"}
                  />

                  <div className="grid grid-cols-2 gap-3 mt-5">
                    <button onClick={() => setModal(null)} disabled={saving}
                      className="py-3 rounded-xl text-[14px] font-semibold text-gray-400 cursor-pointer transition-all active:scale-[0.97] disabled:opacity-50"
                      style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}>
                      Annuler
                    </button>
                    <button
                      onClick={handleSubmitEditTime}
                      disabled={saving}
                      className="py-3 rounded-xl text-[14px] font-bold text-white cursor-pointer transition-all active:scale-[0.97] disabled:opacity-70 flex items-center justify-center gap-2"
                      style={{ background: "linear-gradient(135deg,#0369a1,#06b6d4)", boxShadow: "0 4px 16px rgba(6,182,212,0.25)" }}>
                      {saving && <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />}
                      {saving ? "…" : "Valider"}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
      </CalendarSheet>

      <BottomNav />
    </div>
  )
}

// ============================================
// EVENT CARD (dans day-view)
// ============================================

interface EventCardProps {
  event: CalendarEventRow
  isToday: boolean
  canWrite: boolean
  /** Essai arrivé à son terme : le cadenas mène au bilan, pas à la facturation. */
  trialEnded: boolean
  nextSessionNumber: number | null
  nextSessionLabel: string | null
  nextPsnLoaded: boolean
  onStartSession: () => void
  onEditTime: () => void
  onMove: () => void
  onDelete: () => void
  saving: boolean
}

function EventCard({ event, isToday, canWrite, trialEnded, nextSessionNumber, nextSessionLabel, nextPsnLoaded, onStartSession, onEditTime, onMove, onDelete, saving }: EventCardProps) {
  const navigate = useNavigate()
  const cfg = TYPE_CONFIG[event.type]

  // 3 états pour seance-planned / seance-done avec programme_session_number connu
  const psn = event.programme_session_number ?? null
  const isPlanned  = event.type === "seance-planned"
  const isSeanceDone = event.type === "seance-done"
  // hasPsn requiert que le hook soit résolu — évite d'afficher le bouton pendant le chargement
  // Étendu aux deux types pour que "Séance complétée" s'affiche après soumission questionnaire
  const hasPsn = (isPlanned || isSeanceDone) && psn != null && nextSessionNumber != null && nextPsnLoaded
  // isCompleted : `isSeanceDone` d'abord — c'est le fait établi, posé par `savePostSession`
  // au moment même où le questionnaire est soumis, quel que soit le programme actif
  // AUJOURD'HUI. `nextSessionNumber` (via `getNextSessionNumber`) est, lui, calculé pour
  // le programme COURANT du client : à cheval sur un changement de niveau, une séance
  // "seance-done" du programme précédent (ex. la séance 48 qui vient de déclencher la
  // transition) a un psn qui n'a plus aucun rapport avec le compteur du nouveau
  // programme — `48 < 5` ne dira jamais "complétée" pour la bonne raison qu'aucun des
  // deux nombres ne parle du même programme. Le repli sur la comparaison numérique ne
  // sert donc plus qu'à couvrir la fenêtre d'incohérence éventuelle où `savePostSession`
  // vient de soumettre le questionnaire mais n'a pas encore fini de faire basculer le
  // type de l'événement en base (mise à jour non attendue, cf. commentaire plus haut).
  const isCompleted = (isSeanceDone && psn != null) || (hasPsn && isPlanned && (psn as number) < nextSessionNumber!)
  // isUnlocked / isLocked : uniquement pour seance-planned (gestion du bouton Démarrer)
  const isUnlocked  = isPlanned && hasPsn && (psn as number) === nextSessionNumber!
  const isLocked    = isPlanned && hasPsn && (psn as number) > nextSessionNumber!

  // Gating abonnement : plus de seuil « séance n° ≥ 2 » (D2). Pendant la semaine
  // d'essai toutes les séances sont ouvertes ; une fois l'essai terminé, aucune ne
  // l'est. `canWrite` porte déjà le droit d'accès calculé en base.
  const needsSubscription = !canWrite && isPlanned && !isCompleted
  // psn == null : comportement antérieur conservé (événements sans suivi de programme)
  // On utilise psn == null explicitement pour ne pas confondre avec le loading state
  const canStart    = isToday && (isUnlocked || (isPlanned && psn == null)) && !needsSubscription

  // Activité ajoutée par le client : questionnaire ouvert à l'heure de fin prévue (le
  // push part 30 min après). Une fois répondu, l'activité est close : heure et jour figés.
  const isActivity      = ACTIVITY_EVENT_TYPES.includes(event.type)
  const activityAnswer  = isActivity ? getActivityPostSessionAnswer(event) : null
  const activityEndAt   = isActivity ? getActivityEndAt(event) : null
  const activityLocked  = isActivity && !activityAnswer && activityEndAt != null && activityEndAt.getTime() > Date.now()
  const activityOpen    = isActivity && !activityAnswer && !activityLocked

  const canEditTime = event.type !== "seance-done" && !activityAnswer
  const canDelete   = event.type !== "seance-planned" && event.type !== "seance-done"
  const canMove     = event.type !== "seance-done" && !isCompleted && !activityAnswer

  const WRITE_TITLE = "Abonnement requis pour modifier"

  return (
    <div className="rounded-xl p-4" style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      <div className="flex items-center gap-3 mb-3">
        <TypeGlyph type={event.type} />
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-bold text-white truncate">{event.title}</p>
          <p className="text-[11px] mt-0.5" style={{ color: cfg.color }}>{cfg.label}</p>
        </div>
        {event.end_time && (
          <span title={canEditTime && !canWrite ? WRITE_TITLE : undefined}>
            <button
              onClick={canEditTime && canWrite ? onEditTime : undefined}
              disabled={!canEditTime || !canWrite}
              className={`text-[11px] px-2 py-1 rounded-md flex items-center gap-1 ${canEditTime && canWrite ? "cursor-pointer hover:bg-white/5" : "cursor-default"} ${canEditTime && !canWrite ? "opacity-40" : ""}`}
              style={{ color: cfg.color, background: "rgba(0,0,0,0.2)" }}
            >
              {event.end_time.slice(0, 5)}
              {canEditTime && <Pencil className="w-3 h-3" />}
            </button>
          </span>
        )}
      </div>

      {event.type === "seance-libre" && event.content && (
        <p className="text-[12px] text-gray-300 leading-relaxed whitespace-pre-line line-clamp-6 mb-3 px-1">
          {event.content}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {isCompleted && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg w-full"
            style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.20)" }}>
            <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            <span className="text-[12px] text-emerald-400 font-medium">Séance {psn} complétée</span>
          </div>
        )}
        {activityAnswer?.status === "done" && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg w-full"
            style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.20)" }}>
            <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            <span className="text-[12px] text-emerald-400 font-medium">Questionnaire rempli</span>
          </div>
        )}
        {activityAnswer?.status === "skipped" && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg w-full"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <X className="w-3.5 h-3.5 text-gray-500 shrink-0" />
            <span className="text-[12px] text-gray-500">Séance non réalisée</span>
          </div>
        )}
        {activityLocked && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg w-full"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <Lock className="w-3.5 h-3.5 text-gray-500 shrink-0" />
            <span className="text-[12px] text-gray-500">
              Questionnaire disponible à {formatUnlockTime(activityEndAt!)}
            </span>
          </div>
        )}
        {activityOpen && (
          <button onClick={() => navigate(`/post-session?event=${event.id}`)} disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg cursor-pointer text-[12px] font-bold text-white"
            style={{ background: "linear-gradient(135deg,#0369a1,#06b6d4)" }}>
            <ClipboardList className="w-3.5 h-3.5" />
            Remplir le questionnaire
          </button>
        )}
        {needsSubscription && isPlanned && (
          <button
            onClick={() => navigate(trialEnded ? "/fin-essai" : "/dashboard/billing")}
            className="flex items-center gap-2 px-3 py-2.5 rounded-lg w-full cursor-pointer active:scale-[0.98]"
            style={{ background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.25)" }}
          >
            <Lock className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
            <span className="text-[12px] text-cyan-300 font-medium flex-1 text-left">
              {trialEnded ? "Ta semaine d'essai est terminée" : "Séance réservée aux abonnés"}
            </span>
            <span className="text-[11px] text-cyan-400 font-semibold">
              {trialEnded ? "Mon bilan →" : "S'abonner →"}
            </span>
          </button>
        )}
        {!needsSubscription && isLocked && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg w-full"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <Lock className="w-3.5 h-3.5 text-gray-500 shrink-0" />
            <span className="text-[12px] text-gray-500">
              Termine d'abord :{" "}
              <span className="text-gray-300 font-medium">
                {nextSessionLabel
                  ? `Séance ${nextSessionNumber} — ${nextSessionLabel}`
                  : `Séance ${nextSessionNumber}`}
              </span>
            </span>
          </div>
        )}
        {canStart && (
          <button onClick={onStartSession} disabled={saving}
            className="flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 rounded-lg cursor-pointer text-[12px] font-bold text-white"
            style={{ background: "linear-gradient(135deg,#0369a1,#06b6d4)" }}>
            <Play className="w-3.5 h-3.5" fill="white" />
            Démarrer la séance
          </button>
        )}
        {canMove && (
          <span title={!canWrite ? WRITE_TITLE : undefined} className={!canWrite ? "cursor-not-allowed" : undefined}>
            <button
              onClick={canWrite ? onMove : undefined}
              disabled={saving || !canWrite}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold disabled:opacity-40 ${canWrite ? "cursor-pointer" : "cursor-not-allowed pointer-events-none"}`}
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#e2e8f0" }}
            >
              <Move className="w-3.5 h-3.5" />
              Déplacer
            </button>
          </span>
        )}
        {canDelete && (
          <span title={!canWrite ? WRITE_TITLE : undefined} className={!canWrite ? "cursor-not-allowed" : undefined}>
            <button
              onClick={canWrite ? onDelete : undefined}
              disabled={saving || !canWrite}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold disabled:opacity-40 ${canWrite ? "cursor-pointer" : "cursor-not-allowed pointer-events-none"}`}
              style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.20)", color: "#f87171" }}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Supprimer
            </button>
          </span>
        )}
      </div>
    </div>
  )
}
